import { injectable } from 'inversify';

import { FormatService } from '@vk-hr-tek/core/format';
import { Calendar } from '@vk-hr-tek/core/calendar';

import {
  AbsenceOverlapValidator,
  ExceedingLimitValidator,
  ValuesCompareValidator,
  CheckValueValidator,
} from '@app/gen/events';

import { FormAttribute } from '../types';
import { Validator } from '../responses';

import { EventsValidationService } from './events.validation.service';
import { EventsFormAttributesService } from './events.form.attributes.service';

const VALIDATION_MESSAGES = {
  required: 'Обязательное поле',
  minSpecificWeekendDaysValidator:
    'Выбранный период отпуска не включает необходимое минимальное количество выходных дней',
  vacationMin5DaysValidator:
    'Выбранные период отпуска включает в себя отпуск менее 5 дней между выходными и праздничными днями',
  nonWorkingDaysIncludedCheckValidator:
    'Командировка пересекается с нерабочими днями',
};

@injectable()
export class DateAttributesService {
  constructor(
    private format: FormatService,
    private eventsValidationService: EventsValidationService,
    private calendar: Calendar,
    private eventsFormAttributesService: EventsFormAttributesService,
  ) {}

  private getMinWeekendDaysValidatorData({
    attribute,
    validator,
  }: {
    attribute: FormAttribute;
    validator: Validator | undefined;
  }) {
    const { attributeType, relatedAttribute } = attribute;

    if (attributeType !== 'rangeEnd') return null;

    if (relatedAttribute && validator && 'working_days' in validator) {
      return {
        workingDays: validator.working_days,
        minWeekendDays: validator.min_weekend_days,
      };
    } else {
      return null;
    }
  }

  private getAbsencesOverlapValidatorData({
    attribute,
    validator,
  }: {
    attribute: FormAttribute;
    validator: Validator | undefined;
  }) {
    const { attributeType } = attribute;

    if (attributeType !== 'rangeEnd') return null;

    if (validator && 'existing_dates' in validator) {
      return {
        existingDates: (validator as AbsenceOverlapValidator).existing_dates,
      };
    } else {
      return null;
    }
  }

  private getMinWeekendDaysValidationMessage(
    workingDays: string,
    minWeekendDays: string,
  ) {
    return `При оформлении отпуска на ${workingDays} обязательно наличие ${minWeekendDays}`;
  }

  private separateValidators(validators: Validator[]) {
    return {
      noOverlapValidator: validators.find(({ type }) => type === 'no_overlap'),
      sameMonthValidator: validators.find(({ type }) => type === 'same_month'),
      minWeekendDaysValidator: validators.find(
        ({ type }) => type === 'min_weekend_days',
      ),
      minSpecificWeekendDaysValidator: validators.find(
        ({ type }) => type === 'min_specific_weekend_days',
      ),
      vacationMin5DaysBetweenWeekendsValidator: validators.find(
        ({ type }) => type === 'vacation_min_5_days_between_not_workdays',
      ),
      nonWorkingDaysIncludedCheckValidator: validators.find(
        ({ type }) => type === 'non_working_days_included_check',
      ),
      absencesOverlapValidator: validators.find(
        ({ type }) => type === 'has_absence',
      ),
      valuesCompareValidator: validators.find(
        ({ type }) => type === 'values_compare',
      ),
      onlyCurrentYearValidator: validators.find(
        ({ type }) => type === 'only_current_year',
      ),
      exceedingLimitValidator: validators.find(
        ({ type }) => type === 'exceeding_limit',
      ),
      checkValueValidator: validators.find(
        ({ type }) => type === 'check_value',
      ),
    };
  }

  shouldAttributeBeDisabled({
    attribute,
    initialDisabled,
    relatedAttributeValue,
  }: {
    attribute: FormAttribute;
    initialDisabled: boolean;
    relatedAttributeValue: any;
  }): boolean {
    const { relatedAttribute, attributeType } = attribute;

    if (!relatedAttribute) return initialDisabled;

    const isRangeStartWithValue =
      attributeType === 'rangeStart' && !!relatedAttributeValue;
    const isRangeEndWithoutValue =
      attributeType === 'rangeEnd' && !relatedAttributeValue;

    return isRangeStartWithValue || isRangeEndWithoutValue || initialDisabled;
  }

  getExternalError({
    attribute,
    relatedAttributeValue,
  }: {
    attribute: FormAttribute;
    relatedAttributeValue: any;
  }) {
    const { id, attributeType, validators } = attribute;
    let existingStarts: Date[] = [];

    const { noOverlapValidator, sameMonthValidator, absencesOverlapValidator } =
      this.separateValidators(validators);

    if (
      !(
        attributeType === 'rangeEnd' &&
        relatedAttributeValue &&
        (noOverlapValidator || sameMonthValidator || absencesOverlapValidator)
      )
    ) {
      return '';
    }

    const fromDate = this.calendar.startOfDay(new Date(relatedAttributeValue));

    if (noOverlapValidator && noOverlapValidator.type === 'no_overlap') {
      existingStarts = noOverlapValidator.existing_dates
        .filter(({ from_date }) => new Date(from_date) > fromDate)
        .map(({ from_date }) => new Date(from_date));
    }

    if (
      absencesOverlapValidator &&
      absencesOverlapValidator.type === 'has_absence'
    ) {
      existingStarts = absencesOverlapValidator.existing_dates
        .filter(
          ({ from_date, action }) =>
            action === 'error' && new Date(from_date) > fromDate,
        )
        .map(({ from_date }) => new Date(from_date));
    }

    if (sameMonthValidator && sameMonthValidator.type === 'same_month') {
      existingStarts.push(this.calendar.endOfMonth(fromDate));
    }

    const day = this.calendar.closestTo(fromDate, existingStarts);

    if (!day) return '';

    const disabled = this.eventsValidationService.hasAvailableDatesInRange({
      fromDate,
      toDate: day,
      toAtributeId: id,
      validators,
    });

    return disabled ? 'Нет доступных дат по правилам оформления заявки' : '';
  }

  getValidateArray({
    attribute,
    fieldIndex,
  }: {
    attribute: FormAttribute;
    fieldIndex: string;
  }) {
    const {
      name,
      attributeType,
      required,
      options,
      relatedAttribute,
      nameValidating,
      validators,
    } = attribute;
    const isRangeEnd = attributeType === 'rangeEnd';
    const attributeName = `${fieldIndex}_attribute_${relatedAttribute}`;

    const {
      minWeekendDaysValidator,
      minSpecificWeekendDaysValidator,
      vacationMin5DaysBetweenWeekendsValidator,
      valuesCompareValidator,
      onlyCurrentYearValidator,
      checkValueValidator,
    } = this.separateValidators(validators);

    const minWeekendDaysValidatorData = this.getMinWeekendDaysValidatorData({
      attribute,
      validator: minWeekendDaysValidator,
    });

    const minWeekendDaysValidationMessage = minWeekendDaysValidatorData
      ? this.getMinWeekendDaysValidationMessage(
          this.format.pluralize(minWeekendDaysValidatorData.workingDays, [
            'день',
            'дня',
            'дней',
          ]),
          this.format.pluralize(minWeekendDaysValidatorData.minWeekendDays, [
            'выходного дня',
            'выходных дней',
            'выходных дней',
          ]),
        )
      : '';

    return [
      required
        ? this.eventsFormAttributesService.getRequiredValidator(
            VALIDATION_MESSAGES.required,
            {
              prefix: fieldIndex,
              hideWhen: options?.hide_when,
              showWhen: options?.show_when,
            },
          )
        : undefined,
      minWeekendDaysValidatorData && minWeekendDaysValidationMessage
        ? this.eventsValidationService.minWeekendDaysValidator(
            minWeekendDaysValidationMessage,
            {
              rangeStart: `${fieldIndex}_attribute_${relatedAttribute}`,
              workingDays: minWeekendDaysValidatorData.workingDays,
              minWeekendDays: minWeekendDaysValidatorData.minWeekendDays,
            },
          )
        : undefined,
      minSpecificWeekendDaysValidator && isRangeEnd
        ? this.eventsValidationService.minSpecificWeekendDaysValidator(
            VALIDATION_MESSAGES.minSpecificWeekendDaysValidator,
            attributeName,
          )
        : undefined,
      vacationMin5DaysBetweenWeekendsValidator && isRangeEnd
        ? this.eventsValidationService.vacationMin5DaysBetweenWeekendsValidator(
            VALIDATION_MESSAGES.vacationMin5DaysValidator,
            attributeName,
          )
        : undefined,
      valuesCompareValidator && isRangeEnd
        ? this.eventsValidationService.valuesCompareDatesValidator({
            leftAttribute: attributeName,
            operation: (valuesCompareValidator as ValuesCompareValidator)
              .operation,
            message:
              this.eventsValidationService.createCompareValidatorErrorMessage(
                nameValidating || '',
                name,
                (valuesCompareValidator as ValuesCompareValidator).operation,
              ),
          })
        : undefined,
      onlyCurrentYearValidator
        ? this.eventsValidationService.onlyCurrentYearValidator({
            message: 'Новые даты должны быть указаны в рамках текущего года',
          })
        : undefined,
      checkValueValidator
        ? this.eventsValidationService.checkValueDateValidator({
            message:
              this.eventsValidationService.createCheckValueDatesValidatorErrorMessage(
                name || '',
                (checkValueValidator as CheckValueValidator).value,
                (checkValueValidator as CheckValueValidator).operation,
              ),
            operation: (checkValueValidator as CheckValueValidator).operation,
            valueToCompare: (checkValueValidator as CheckValueValidator).value,
          })
        : undefined,
    ].filter(Boolean);
  }

  getWarningsArray({
    attribute,
    fieldIndex,
  }: {
    attribute: FormAttribute;
    fieldIndex: string;
  }) {
    const { relatedAttribute, attributeType, validators } = attribute;
    const isRangeEnd = attributeType === 'rangeEnd';
    const attributeName = `${fieldIndex}_attribute_${relatedAttribute}`;

    const {
      nonWorkingDaysIncludedCheckValidator,
      absencesOverlapValidator,
      exceedingLimitValidator,
    } = this.separateValidators(validators);

    const absencesOverlapValidatorData = this.getAbsencesOverlapValidatorData({
      attribute,
      validator: absencesOverlapValidator,
    });

    const isVacationAvailableDaysFieldExist =
      exceedingLimitValidator &&
      typeof (exceedingLimitValidator as ExceedingLimitValidator)
        .vacation_available_days !== undefined;

    return [
      nonWorkingDaysIncludedCheckValidator && isRangeEnd
        ? this.eventsValidationService.nonWorkingDaysIncludedCheckValidator(
            VALIDATION_MESSAGES.nonWorkingDaysIncludedCheckValidator,
            {
              rangeStart: attributeName,
            },
          )
        : undefined,
      absencesOverlapValidator && isRangeEnd
        ? this.eventsValidationService.absencesOverlapValidator({
            rangeStart: `${fieldIndex}_attribute_${relatedAttribute}`,
            existingDates: absencesOverlapValidatorData?.existingDates || [],
          })
        : undefined,
      isVacationAvailableDaysFieldExist && isRangeEnd
        ? this.eventsValidationService.exceedingLimitValidator({
            rangeStart: `${fieldIndex}_attribute_${relatedAttribute}`,
            availableVacationDays: (
              exceedingLimitValidator as ExceedingLimitValidator
            ).vacation_available_days,
          })
        : undefined,
    ].filter(Boolean);
  }
}
